import type { CompoundSelectSettingType, SelectOptionType, SelectValueType } from '../../type';
import type { PayloadControlChange } from '~/presentation/reports/common/type';

import { WrapperActivator } from '../../SelectCompound/components/WrapperActivator';
import { useControlDataChange } from '~/presentation/reports/common';
import { getSelectLabel } from '../../SelectCompound/utils';
import { SelectPopover } from '../../SelectPopover';
import { useApplyCampaignDate } from '../hooks';
import { TabContainer } from './TabContainer';
import { useEffect, useState } from 'react';

export interface ISelectCampaignContent {
  config: CompoundSelectSettingType;
  optionsMap: Record<string, SelectOptionType[]>;
}

export const SelectCampaignContent: React.FC<ISelectCampaignContent> = ({ config, optionsMap }) => {
  const { setDateByCampaignId } = useApplyCampaignDate();

  const [activePopover, setActivePopover] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState(config.defaultTab || '');
  const [tempLabel, setTempLabel] = useState<string>();

  const { defaultValue } = config;
  const tabs = config.tabs || [];
  const options = optionsMap[selectedTab] || [];

  const label = getSelectLabel(defaultValue, options, tempLabel);

  const { emitControlChange } = useControlDataChange();

  const togglePopover = () => {
    setActivePopover(!activePopover);
  };

  const onChangeValue = (
    id: string,
    value: SelectValueType,
    tabs: {
      id: string;
      content: string;
    }[],
  ) => {
    const newValues = tabs.reduce((acc, tab) => {
      const newValue = selectedTab === tab.id ? value : undefined;
      acc[tab.id] = newValue;
      return acc;
    }, {} as PayloadControlChange);
    emitControlChange({ ...newValues, [id]: value });

    if (typeof value !== 'string') return;
    setDateByCampaignId(value);
  };

  useEffect(() => {
    setTempLabel(label);
  }, [label]);

  useEffect(() => {
    if (!config.defaultTab) return;

    setSelectedTab(config.defaultTab);
  }, [config.defaultTab]);

  return (
    <SelectPopover
      activator={<WrapperActivator label={label} onClick={togglePopover} />}
      popoverActive={activePopover}
      togglePopoverActive={togglePopover}
      {...config}
      options={options}
      onChangeValue={(id, value) => onChangeValue(id, value, tabs)}
      header={
        <TabContainer selectedTab={selectedTab} tabs={tabs} setSelectedTab={setSelectedTab} />
      }
      maxHeight={'180px'}
      minHeight={'180px'}
      // Polaris caps popover content at 25rem; wider gets clipped, narrower pushes tabs into "More views".
      width={'400px'}
    />
  );
};
